import axios from "axios";
import { getApiBaseUrl } from "./httpServices";
import { IMP_KEY } from "../components/common/ImpersonationBanner";

/**
 * Starting and ending a read-only impersonation session.
 *
 * The operator's own token is stashed before it is replaced, so exiting restores
 * the session they came from rather than dropping them at a login screen. Both
 * live in localStorage because impersonation reloads the app into another user's
 * session — anything held in memory is gone by then.
 */
/**
 * Where a given role lands after signing in.
 *
 * Mirrors the switch in the login page. Impersonation previously redirected to
 * "/dashboard", which is not a route — the session started correctly and then
 * showed a 404, which looks like impersonation failing when it had already
 * worked.
 */
export function landingFor(role) {
  switch (String(role)) {
    case "4": return "/admin/state-dashboard";
    case "admin": return "/admin/admin-dashboard";
    // Roles 1, 2 and 3 all land here, as they do after a normal sign-in.
    default: return "/main-dashboard";
  }
}

const ImpersonationServices = {
  /**
   * Swap the current session for a read-only view of another user.
   * Reloads on success: every screen reads the token at mount.
   */
  start: async ({ userId, email, reason, name, schoolName }) => {
    const base = getApiBaseUrl().replace(/\/+$/, "");
    const { data } = await axios.post(
      `${base}/admin/impersonate`,
      { userId, email, reason },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    if (!data || !data.success) {
      throw new Error((data && data.error) || "Could not start the session.");
    }

    const expiresAt = new Date(Date.now() + (data.expiresInMinutes || 30) * 60000).toISOString();

    localStorage.setItem(IMP_KEY, JSON.stringify({
      // Kept so Exit returns the operator to their own session rather than to a
      // login screen.
      previousToken: localStorage.getItem("token"),
      previousRole: localStorage.getItem("userrole"),
      previousUser: localStorage.getItem("user"),
      sessionId: data.sessionId,
      expiresAt,
      name: name || (data.target && data.target.name),
      email: email || (data.target && data.target.email),
      schoolName: schoolName || null
    }));

    localStorage.setItem("token", data.token);
    if (data.target && data.target.role) localStorage.setItem("userrole", String(data.target.role));

    // Handed back so the caller can navigate without repeating the role logic.
    return { ...data, landing: landingFor(data.target && data.target.role) };
  }
};

export default ImpersonationServices;
